import type { BalanceProjectionData } from '../../../hooks/useReports';

interface Props {
  projection: BalanceProjectionData;
  loading: boolean;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const BalanceProjection = ({ projection, loading }: Props) => {
  if (loading) return <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />;

  const { currentBalance, expectedIncome, pendingExpenses, overdueAmount, estimatedEndBalance, riskLevel } = projection;

  const riskConfig = {
    safe:      { label: 'Projeção Saudável',  color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', bar: 'bg-emerald-500', icon: 'ri-shield-check-line' },
    attention: { label: 'Atenção ao Saldo',   color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200',   bar: 'bg-amber-500',   icon: 'ri-error-warning-line' },
    danger:    { label: 'Risco de Negativo',  color: 'text-red-600',     bg: 'bg-red-50',     border: 'border-red-200',     bar: 'bg-red-500',     icon: 'ri-alarm-warning-line' },
  };

  const rc = riskConfig[riskLevel];
  const maxVal = Math.max(currentBalance, expectedIncome, pendingExpenses, Math.abs(estimatedEndBalance), 1);

  const steps = [
    { label: 'Saldo atual', value: currentBalance, color: 'bg-gray-400', positive: currentBalance >= 0 },
    { label: 'Entradas previstas', value: expectedIncome, color: 'bg-emerald-400', positive: true },
    { label: 'Saídas pendentes', value: pendingExpenses, color: 'bg-orange-400', positive: false },
    { label: 'Saldo estimado', value: estimatedEndBalance, color: rc.bar, positive: estimatedEndBalance >= 0 },
  ];

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="text-[15px] font-700 text-gray-900">Projeção de Saldo</h3>
          <p className="text-[12px] text-gray-400 mt-0.5">Estimativa até o fim do mês com base em pendências</p>
        </div>
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-[12px] font-600 ${rc.bg} ${rc.border} ${rc.color}`}>
          <i className={`${rc.icon} text-[14px]`} />
          {rc.label}
        </div>
      </div>

      {/* Visual waterfall */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {steps.map((step) => {
          const barHeight = Math.max(8, (Math.abs(step.value) / maxVal) * 80);
          return (
            <div key={step.label} className="flex flex-col items-center gap-2">
              <span className={`text-[13px] font-700 ${step.positive ? (step.value < 0 ? 'text-red-600' : 'text-gray-800') : 'text-red-500'}`}>
                {fmt(step.value)}
              </span>
              <div className="w-full flex items-end justify-center" style={{ height: 88 }}>
                <div
                  className={`w-full rounded-xl transition-all ${step.color}`}
                  style={{ height: `${barHeight}px` }}
                />
              </div>
              <span className="text-[10px] text-gray-500 text-center leading-tight">{step.label}</span>
            </div>
          );
        })}
      </div>

      {/* Arrow flow */}
      <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 text-[12px]">
        <div className="flex items-center gap-1.5">
          <span className="font-500 text-gray-500">Atual</span>
          <span className="font-700 text-gray-800">{fmt(currentBalance)}</span>
        </div>
        <i className="ri-add-line text-gray-400 text-[11px]" />
        <div className="flex items-center gap-1.5">
          <span className="font-500 text-emerald-600">+{fmt(expectedIncome)}</span>
        </div>
        <i className="ri-subtract-line text-gray-400 text-[11px]" />
        <div className="flex items-center gap-1.5">
          <span className="font-500 text-orange-500">-{fmt(pendingExpenses)}</span>
        </div>
        <i className="ri-equal-line text-gray-400 text-[11px]" />
        <div className="flex items-center gap-1.5">
          <span className={`font-700 ${estimatedEndBalance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(estimatedEndBalance)}</span>
        </div>
      </div>

      {overdueAmount > 0 && (
        <div className="mt-3 flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-[12px] text-red-600">
          <i className="ri-alarm-warning-line text-[14px]" />
          <span>Atenção: <strong>{fmt(overdueAmount)}</strong> em lançamentos vencidos não inclusos na projeção acima.</span>
        </div>
      )}
    </div>
  );
};
